import { IRebaseOptions } from '../models/data/IRebaseOptions';
import { sortByDate } from '../utils/collection';
import { isNullOrEmpty, isNullOrUndefined } from '../utils/typeChecks';
import { TRebaseModifier } from '../models/data/TRebaseModifier';
import { IRebaseSeries } from '../models/data/IRebaseSeries';

export class RebaseHelper {
	options: IRebaseOptions;

	constructor(options: IRebaseOptions) {
		this.options = options;
	}

	/**
	 * Check whether rebase should be applied
	 * @param collection
	 */
	mustPerformRebase(collection: any[]) {
		const { enabled, series } = this.options;
		return enabled && !isNullOrEmpty(series) && !isNullOrEmpty(collection);
	}

	/**
	 * Apply rebase to a data collection
	 * @param collection
	 */
	rebase(collection: any[]) {
		const { series } = this.options;
		if (this.mustPerformRebase(collection)) {
			collection = sortByDate(collection, (item: any) => item.date);
			collection = this.applyInceptionInjection(collection);
			series.forEach((seriesInstance: IRebaseSeries) => {
				collection = this.orchestrateSeries(collection, seriesInstance);
				collection = this.rebaseSeries(collection, seriesInstance);
			});
		}
		return collection;
	}

	applyInceptionInjection(collection: any[]): any[] {
		const { inceptionInjection } = this.options;
		if (inceptionInjection) {
			const { enabled, forceUniqueInceptionRecord, inceptionDate } = inceptionInjection;
			if (enabled) {
				const mustInject = !forceUniqueInceptionRecord || !collection.some(item => item.date === inceptionDate);
				if (mustInject) {
					collection.unshift({ date: inceptionDate });
				}
			}
		}
		return collection;
	}

	/**
	 * Convert series values to change values if the series type is 'value'
	 * Apply the value modifier if one exists
	 * @param collection
	 * @param series
	 */
	orchestrateSeries(collection: any[], series: IRebaseSeries) {
		const { key, valueModifier, type = 'change' } = series;
		if (!isNullOrEmpty(collection)) {
			for (let i = collection.length - 1; i > 0; i--) {
				const currentValue = collection[i][key];
				const previousValue = collection[i - 1][key];
				if (type === 'value' && previousValue !== 0) {
					collection[i][key] = (currentValue / previousValue) - 1;
				}
				if (!isNullOrUndefined(valueModifier)) {
					collection[i][key] = this.applyModifier(currentValue, valueModifier);
				}
			}
		}
		return collection;
	}

	/**
	 * Run the base logic through the values in a series
	 * @param collection
	 * @param key
	 */
	rebaseSeries(collection: any[], series: IRebaseSeries) {
		const { key } = series;
		const startValue = series.startValue || this.options.startValue || 100;
		if (!isNullOrEmpty(collection)) {
			collection[0][key] = startValue;

			for (let i = 1; i < collection.length; i++) {
				const previous = collection[i - 1];
				const delta = collection[i];

				if (Number.isNaN(delta[key]) || isNullOrUndefined(delta[key])) {
					delta[key] = previous[key];
				}
				else {
					const deltaValue = delta[key];
					delta[key] = previous[key] * (1 + deltaValue);
				}
			}
		}
		return collection;
	}

	/**
	 * Apply the given value modifier
	 * @param rawValue
	 * @param valueModifier
	 */
	applyModifier(rawValue: number, valueModifier: TRebaseModifier): number {
		if (valueModifier) {
			if (valueModifier === 'divideBy100') {
				return rawValue / 100;
			}
			if (valueModifier === 'multiplyBy100') {
				return rawValue * 100;
			}
			return valueModifier(rawValue);
		}
		return rawValue;
	}
}